import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  MagnifyingGlassIcon, 
  UserIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  CogIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../shared/services/adminApi'
import { SystemStatusCard, AdminHeader } from '../../../shared/components'
import AdminPlanChangeModal from '../components/modals/AdminPlanChangeModal'
import PlanHistoryModal from '../components/modals/PlanHistoryModal'
import toast from 'react-hot-toast'
import type { SystemHealth } from '../../../shared/services/adminApi'

interface SimpleUser {
  _id: string
  email: string
  name?: string
  role: string
  isActive: boolean
  emailVerified?: boolean // Add email verification status
  plan: string
  monthlyLinkLimit: number
  monthlyClickLimit: number
  createdAt: string
  lastLoginAt?: string
  stats: {
    totalUrls: number
    totalClicks: number
  }
}

interface DeletionPreview {
  user: {
    id: string
    email: string
    name?: string
    createdAt: string
    plan: string
  }
  deletionPreview: {
    urlsToDelete: number
    clicksToDelete: number
    dateRanges: {
      firstUrlCreated: string | null
      lastUrlCreated: string | null
      firstClick: string | null
      lastClick: string | null
    }
    recentUrls: Array<{
      shortCode: string
      originalUrl: string
      clicks: number
      createdAt: string
    }>
  }
  warning: string
}

export default function UserManagementPage() {
  const [users, setUsers] = useState<SimpleUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState<string | null>(null)
  const [deletionPreview, setDeletionPreview] = useState<DeletionPreview | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false)
  const [showPlanHistoryModal, setShowPlanHistoryModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<SimpleUser | null>(null)
  
  // Filter states
  const [filters, setFilters] = useState({
    plan: 'all',
    isActive: 'all',
    isVerified: 'all',
    role: 'all'
  })

  const loadUsers = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getUsers({ 
        page, 
        limit: 20, 
        search: search || undefined,
        plan: filters.plan !== 'all' ? filters.plan : undefined,
        isActive: filters.isActive !== 'all' ? filters.isActive : undefined,
        isVerified: filters.isVerified !== 'all' ? filters.isVerified : undefined,
        role: filters.role !== 'all' ? filters.role : undefined
      })
      // Ensure required fields are present (using actual plan limits, not hardcoded values)
      const usersWithRequiredFields = data.users.map((user: any) => ({
        ...user,
        // Use actual plan limits from backend, with fallback only for type safety
        monthlyLinkLimit: user.monthlyLinkLimit ?? 50,
        monthlyClickLimit: user.monthlyClickLimit ?? 1000,
        emailVerified: user.emailVerified || false
      }))
      setUsers(usersWithRequiredFields)
      setTotalPages(data.pagination.totalPages)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const loadSystemHealth = async () => {
    try {
      const healthData = await adminApiService.getSystemHealth()
      setSystemHealth(healthData)
    } catch (error) {
      console.error('Failed to load system health:', error)
    }
  }

  const handleRefreshAll = async () => {
    try {
      setIsRefreshingStatus(true)
      await Promise.all([
        loadUsers(),
        loadSystemHealth()
      ])
      toast.success('Data refreshed successfully')
    } catch (error) {
      console.error('Failed to refresh data:', error)
      toast.error('Failed to refresh data')
    } finally {
      setIsRefreshingStatus(false)
    }
  }

  useEffect(() => {
    loadUsers()
    loadSystemHealth()
  }, [page, search, filters])

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
    setPage(1) // Reset to first page when filter changes
  }

  const clearFilters = () => {
    setFilters({
      plan: 'all',
      isActive: 'all',
      isVerified: 'all',
      role: 'all'
    })
    setPage(1)
  }

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await adminApiService.updateUserStatus(userId, isActive)
      await loadUsers()
      toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`)
    } catch (error) {
      console.error('Failed to update user status:', error)
      toast.error('Failed to update user status')
    }
  }

  const handleDeleteClick = async (userId: string) => {
    try {
      const preview = await adminApiService.getUserDeletionPreview(userId)
      setDeletionPreview(preview)
      setUserToDelete(userId)
      setShowDeleteModal(true)
    } catch (error) {
      console.error('Failed to get deletion preview:', error)
      toast.error('Failed to get deletion preview')
    }
  }

  const handleConfirmDelete = async () => {
    if (!userToDelete) {return}

    try {
      setIsDeleting(true)
      const result = await adminApiService.deleteUser(userToDelete, true)
      
      toast.success(
        `User deleted successfully. Removed ${result.deletedCounts.urls} links and ${result.deletedCounts.clicks} clicks.`
      )
      
      setShowDeleteModal(false)
      setUserToDelete(null)
      setDeletionPreview(null)
      await loadUsers()
    } catch (error: any) {
      console.error('Failed to delete user:', error)
      toast.error(error.response?.data?.message || 'Failed to delete user')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to delete')
      return
    }

    if (!confirm(`Are you sure you want to delete ${selectedUsers.length} users? This action cannot be undone.`)) {
      return
    }

    try {
      setIsDeleting(true)
      const result = await adminApiService.bulkDeleteUsers(selectedUsers, true)
      
      toast.success(
        `${result.deletedCounts.users} users deleted successfully. Removed ${result.deletedCounts.urls} links and ${result.deletedCounts.clicks} clicks.`
      )
      
      setSelectedUsers([])
      await loadUsers()
    } catch (error: any) {
      console.error('Failed to bulk delete users:', error)
      toast.error(error.response?.data?.message || 'Failed to delete users')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    const nonAdminUsers = users.filter(user => user.role !== 'admin')
    if (selectedUsers.length === nonAdminUsers.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(nonAdminUsers.map(user => user._id))
    }
  }

  const handlePlanChange = (user: SimpleUser) => {
    setSelectedUser(user)
    setShowPlanChangeModal(true)
  }

  const handlePlanHistory = (user: SimpleUser) => {
    setSelectedUser(user)
    setShowPlanHistoryModal(true)
  }

  const handlePlanChangeSuccess = () => {
    loadUsers()
    setShowPlanChangeModal(false)
    setSelectedUser(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader 
        title="User Management"
        subtitle="Manage user accounts, permissions, and access levels"
        showRefresh={true}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshingStatus}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SystemStatusCard 
          systemHealth={systemHealth}
          onRefresh={handleRefreshAll}
          isRefreshing={isRefreshingStatus}
          className="mb-6"
        />

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800 dark:text-blue-200">
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedUsers([])}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  Clear selection
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name, email, or username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            {/* Filter Toggle Button */}
            <div className="flex items-center">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium ${
                  showFilters 
                    ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                } hover:bg-gray-50 dark:hover:bg-gray-700`}
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Plan Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Plan
                  </label>
                  <select
                    value={filters.plan}
                    onChange={(e) => handleFilterChange('plan', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Plans</option>
                    <option value="free">Free</option>
                    <option value="growth">Growth</option>
                    <option value="scale">Scale</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Active Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Active Status
                  </label>
                  <select
                    value={filters.isActive}
                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {/* Email Verification Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Status
                  </label>
                  <select
                    value={filters.isVerified}
                    onChange={(e) => handleFilterChange('isVerified', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Status</option>
                    <option value="true">Verified</option>
                    <option value="false">Not Verified</option>
                  </select>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Roles</option>
                    <option value="user">Users</option>
                    <option value="admin">Admins</option>
                  </select>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {Object.values(filters).some(value => value !== 'all') && (
                    <span>Filters applied</span>
                  )}
                </div>
                <button
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200"
                >
                  Clear all filters
                </button>
              </div>
            </motion.div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your search criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === users.filter(u => u.role !== 'admin').length && users.filter(u => u.role !== 'admin').length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Email Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Stats
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user, index) => (
                    <motion.tr
                      key={user._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.role !== 'admin' && (
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user._id)}
                            onChange={() => handleSelectUser(user._id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <UserIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <Link 
                              to={`/users/${user._id}`}
                              className="text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                            >
                              {user.name || user.email.split('@')[0]}
                            </Link>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.emailVerified ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                              <span className="text-sm text-green-700 dark:text-green-400">Verified</span>
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                              <span className="text-sm text-red-700 dark:text-red-400">Not Verified</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                            {user.plan}
                          </span>
                          <button
                            onClick={() => handlePlanChange(user)}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
                            title="Change plan"
                          >
                            <CogIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handlePlanHistory(user)}
                            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                            title="View plan history"
                          >
                            <ClockIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div>
                          {user.stats.totalUrls} links
                        </div>
                        <div className="text-xs">
                          {user.stats.totalClicks} clicks
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleStatusChange(user._id, !user.isActive)}
                            className={`text-sm font-medium ${
                              user.isActive
                                ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          {user.role !== 'admin' && (
                            <button
                              onClick={() => handleDeleteClick(user._id)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete user"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletionPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="ml-4 text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Delete User Account
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      You are about to permanently delete the account for{' '}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {deletionPreview.user.name || deletionPreview.user.email}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false)
                    setUserToDelete(null)
                    setDeletionPreview(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500 dark:hover:bg-gray-700"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Change Modal */}
      {selectedUser && (
        <AdminPlanChangeModal
          isOpen={showPlanChangeModal}
          onClose={() => {
            setShowPlanChangeModal(false)
            setSelectedUser(null)
          }}
          user={selectedUser}
          onSuccess={handlePlanChangeSuccess}
        />
      )}

      {/* Plan History Modal */}
      {selectedUser && (
        <PlanHistoryModal
          isOpen={showPlanHistoryModal}
          onClose={() => {
            setShowPlanHistoryModal(false)
            setSelectedUser(null)
          }}
          userId={selectedUser._id}
          userEmail={selectedUser.email}
        />
      )}
    </div>
  )
}
